import React, { useState } from "react";
import { useLang } from "@/i18n";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BookOpen, Check } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";

export default function Recipes() {
  const { t, tt } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/subscribers", { email, name, source: "recipes" });
      setDone(true);
      toast.success(t("recipes.thanks"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <div>
      <SEO title={t("nav.recipes")} description={tt({
        tr: "Ariaf mutfağının 10 imza yemeği — ücretsiz PDF.",
        ar: "10 وصفات مميزة من مطبخ أرياف — PDF مجاني.",
        en: "10 signature recipes from Ariaf's kitchen — free PDF.",
        ru: "10 фирменных рецептов от кухни Ariaf — бесплатный PDF.",
      })} />
      <div className="section-navy py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.35em] text-brand-gold mb-4">Ariaf</div>
          <h1 className="font-serif text-5xl md:text-6xl leading-tight" data-testid="recipes-title">
            {tt({
              tr: "Ariaf Mutfağının 10 İmza Yemeği",
              ar: "10 وصفات مميزة من مطبخ أرياف",
              en: "10 Signature Recipes from Ariaf's Kitchen",
              ru: "10 фирменных рецептов от кухни Ariaf",
            })}
          </h1>
          <p className="mt-4 text-canvas-cream/80 max-w-2xl">
            {tt({
              tr: "Adana kebabından mansafa, baklavadan mandiye — evinizde adım adım pişirebileceğiniz 10 tarif. Ücretsiz PDF olarak size gönderelim.",
              ar: "من كباب أضنة إلى المنسف، من البقلاوة إلى المندي — 10 وصفات يمكنك تحضيرها خطوة بخطوة في المنزل. سنُرسلها لك كملف PDF مجاناً.",
              en: "From Adana kebab to mansaf, from baklava to mandi — 10 recipes you can cook at home step by step. We'll send you the free PDF.",
              ru: "От адана-кебаба до мансафа, от пахлавы до манди — 10 рецептов, которые вы можете приготовить дома шаг за шагом. Мы отправим вам PDF бесплатно.",
            })}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        {done ? (
          <div className="bg-white rounded-2xl p-10 border border-brand-gold/30 shadow-sm text-center" data-testid="recipes-success">
            <div className="w-16 h-16 rounded-full bg-brand-orange/10 flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-brand-orange" />
            </div>
            <h2 className="font-serif text-3xl text-brand-navy">{t("recipes.thanks")}</h2>
            <p className="mt-2 text-slate-600">{tt({
              tr: "PDF birazdan e-postanıza gönderilecek. Spam kutunuzu kontrol etmeyi unutmayın.",
              ar: "سيُرسل PDF إلى بريدك الإلكتروني قريباً. تحقق من مجلد الرسائل غير المرغوب فيها.",
              en: "The PDF will arrive in your inbox shortly. Please check your spam folder.",
              ru: "PDF придёт на вашу почту в ближайшее время. Проверьте папку «Спам».",
            })}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl p-8 border border-brand-gold/30 shadow-sm grid gap-4" data-testid="recipes-form">
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="text-brand-orange" size={20} />
              <h2 className="font-serif text-2xl text-brand-navy">{t("recipes.get_pdf")}</h2>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="r-name">{t("form.name")}</Label>
              <Input id="r-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="recipes-name" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="r-email">{t("form.email")}</Label>
              <Input id="r-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="recipes-email" />
            </div>
            <Button type="submit" className="bg-brand-orange hover:bg-brand-orange-dark text-white rounded-full mt-2" data-testid="recipes-submit">
              {t("recipes.download")}
            </Button>
            <p className="text-[11px] text-slate-500 text-center mt-1">
              {tt({
                tr: "E-postanızı sadece PDF göndermek için kullanırız. İstediğiniz zaman abonelikten çıkabilirsiniz.",
                ar: "نستخدم بريدك فقط لإرسال الـ PDF. يمكنك إلغاء الاشتراك في أي وقت.",
                en: "We use your email only to send the PDF. Unsubscribe any time.",
                ru: "Мы используем вашу почту только для отправки PDF. Отписаться можно в любое время.",
              })}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
